import React, { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';
import { Grid, Paper, Typography } from '@material-ui/core';

import HeaderBackButton, {
  StyledPageTemplate
} from '../../../common/HeaderBackButton';
import PageTemplateBody from '../../../common/page-template/PageTemplateBody';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../../common/page-template/PageTemplateHeader';
import SplitPane, { Pane } from '../../../common/SplitPane';
import SubTaskListCard from '../../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../../tasks/cards/TaskAssignCard';
import TaskStatusCard from '../../tasks/cards/TaskStatusCard';
import {
  convertToProductExportEntity,
  getColumnTitle,
  ProductBoardColumnEntity
} from '../board/ProductBoardColumn';
import ProductExportButton from '../components/ProductExportButton';
import { getProductEntityPrefix } from '../Product.hooks';
import {
  Product,
  ProductEntity,
  ProductExportEntity,
  ProductRisk,
  ProductUserNeed
} from '../Product.interface';

const StyledBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .document-pane {
    flex: 1;
    overflow: auto;
  }
  .detail-pane {
    padding: 12px;
    overflow: auto;
    .content {
      height: fit-content;
    }
  }
  .product-document {
    max-width: 768px;
    margin: 64px auto;
    .document-section {
      width: 100%;
      padding: 24px 48px;
      border-radius: 0;
      transition: all 300ms;
      &:first-child {
        padding-top: 64px;
        border-radius: 4px 4px 0 0;
      }
      &:last-child {
        padding-bottom: 64px;
        border-radius: 0 0 4px 4px;
      }
      &:hover .section-container::before {
        content: '';
        display: block;
        position: absolute;
        top: -10px;
        left: -10px;
        bottom: -10px;
        right: -10px;
        background: #ecf5ff;
        border: solid 2px #d0e5ff;
        border-radius: 4px;
        z-index: -1;
      }
      &.active {
        box-shadow: none;
        background: transparent;
        padding: 0;
        margin: 32px 0;
      }
    }
    .section-container {
      position: relative;
      z-index: 0;
    }
    img {
      max-width: 100%;
    }
    .risk {
      margin-top: 12px;
    }
  }
`;

export interface ProductDocumentCommonPageProps {
  product?: Product;
  productEntities?: ProductEntity[];
  entity: ProductBoardColumnEntity;
  renderInfoCard: (
    productEntity: ProductEntity,
    title: string,
    hidden?: boolean
  ) => JSX.Element;
  renderGridCards: (productEntity: ProductEntity) => JSX.Element;
}

const ProductDocumentCommonPage: React.FC<ProductDocumentCommonPageProps> = (
  props: ProductDocumentCommonPageProps
) => {
  const {
    product,
    productEntities,
    entity,
    renderInfoCard,
    renderGridCards
  } = props;
  const { search } = useLocation();
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [from = 'detail'] = useQueryParam('from', StringParam);
  // use index instead of object
  // since selected object changes reference on update
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const params: string = useMemo(() => {
    const params: URLSearchParams = new URLSearchParams(search);
    params.delete('from');
    return '?' + params.toString();
  }, [search]);

  const selected: ProductEntity | undefined = useMemo(() => {
    return productEntities?.[selectedIndex];
  }, [productEntities, selectedIndex]);

  const prefix: string = useMemo(() => {
    return getProductEntityPrefix(entity);
  }, [entity]);

  const exportEntity: ProductExportEntity = useMemo(() => {
    return convertToProductExportEntity(entity);
  }, [entity]);

  const entityTitle: string = useMemo(() => {
    const title: string = getColumnTitle(entity);
    return title + 's';
  }, [entity]);

  return (
    <StyledPageTemplate>
      <PageTemplateHeader>
        <HeaderBackButton
          to={`/portal/products/${product?.id || 0}/${from}${params}`}
        />
        <h2>
          {product?.name} / {entityTitle}
        </h2>
        <div style={{ flex: 1 }} />
        <PageTemplateHeaderActions>
          {product && (
            <ProductExportButton product={product} entity={exportEntity} />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateBody>
        <StyledBody>
          <SplitPane primary='next'>
            <Pane
              className='document-pane'
              innerRef={paneRef}
              onClick={event => {
                const { target } = event;
                // only remove selected when clicking itself directly
                if (!target || target === paneRef.current) {
                  setSelectedIndex(-1);
                }
              }}
            >
              <div className='product-document'>
                {productEntities?.map(
                  (productEntity: ProductEntity, index: number) => {
                    const id: string = `${prefix}${productEntity.id}`;
                    const title: string = `${id} ${productEntity.title}`;
                    const isSelected: boolean =
                      productEntity.id === selected?.id;
                    const risk: ProductRisk | undefined =
                      (productEntity as ProductUserNeed).risk || undefined;
                    return (
                      <Paper
                        key={productEntity.id}
                        className={`document-section${
                          isSelected ? ' active' : ''
                        }`}
                      >
                        {renderInfoCard(productEntity, id, !isSelected)}
                        {!isSelected && (
                          <div
                            className='section-container'
                            onClick={event => {
                              event.stopPropagation();
                              setSelectedIndex(index);
                            }}
                          >
                            <Typography variant='h5'>{title}</Typography>

                            {risk && (
                              <Typography variant='subtitle1' className='risk'>
                                Risk:{' '}
                                {typeof risk === 'number'
                                  ? ProductRisk[risk]
                                  : risk}
                              </Typography>
                            )}

                            <div
                              className='description'
                              dangerouslySetInnerHTML={{
                                __html: productEntity.description
                              }}
                            />
                          </div>
                        )}
                      </Paper>
                    );
                  }
                )}
              </div>
            </Pane>
            {selected?.task && (
              <SplitPane width='30%' className='detail-pane'>
                <Grid container spacing={2} className='content'>
                  <Grid item xs={12}>
                    <TaskStatusCard task={selected.task} />
                  </Grid>
                  {selected.task?.children && (
                    <Grid item xs={12}>
                      <SubTaskListCard
                        title='Stakeholders'
                        task={selected.task}
                        disabled
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TaskAssignCard task={selected.task} />
                  </Grid>
                  {renderGridCards(selected)}
                </Grid>
              </SplitPane>
            )}
          </SplitPane>
        </StyledBody>
      </PageTemplateBody>
    </StyledPageTemplate>
  );
};

export default ProductDocumentCommonPage;
